import { Fragment } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from '@mui/material';
import type { LegalTable } from '../../../domain/entities';

interface LegalSectionProps {
  title: string;
  content: string;
  table?: LegalTable;
}

/**
 * Converts URLs and emails in text to clickable links
 */
const parseContentWithLinks = (text: string): React.ReactNode[] => {
  // Regex to match URLs and emails
  const linkRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const [fullMatch, url, email] = match;

    if (url) {
      parts.push(
        <Link href={url} key={match.index} rel="noopener noreferrer" target="_blank">
          {url}
        </Link>,
      );
    } else if (email) {
      parts.push(
        <Link href={`mailto:${email}`} key={match.index}>
          {email}
        </Link>,
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};

/**
 * Content section for legal documents
 */
export const LegalSection = ({ title, content, table }: LegalSectionProps) => {
  const parsedContent = parseContentWithLinks(content);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="h2" gutterBottom variant="h6">
        {title}
      </Typography>
      <Typography component="div" sx={{ whiteSpace: 'pre-line' }} variant="body1">
        {parsedContent.map((part, index) => (
          <Fragment key={index}>{part}</Fragment>
        ))}
      </Typography>
      {table && (
        <TableContainer component={Paper} sx={{ mt: 2 }} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {table.headers.map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 'bold' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row, index) => (
                <TableRow key={index}>
                  {table.headers.map((header) => (
                    <TableCell key={header}>{row[header]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
